/**
 * @file PlanAttentionStrip.jsx
 * @module ui/src/components/admin/PlanAttentionStrip
 * @description
 *   Aggregate server-supplied plan and attention counts rendered as an interactive
 *   summary strip. Counts are never derived from rendered rows — all figures come
 *   from the RegistrySummaryPayload prop supplied by PlanRegistry (C-01).
 *   Activating a count segment delegates the corresponding filter update to the
 *   parent registry via onFilterApply.
 *
 * @version 1.0.2
 * @since S756
 * @author data-fit LLC · DecisionFlow · S777
 *
 * @architecture
 *   Locked decisions : #1, #2
 *   Temporal invariants : INV-3
 *   Design principles : DP-5, DP-14
 *
 * @domain-rules
 *   - Counts are server-supplied via RegistrySummaryPayload — never derived client-side from plan rows.
 *   - Activating a count calls onFilterApply only — no direct API calls from this component.
 *   - temporalRelationship filter values are passed verbatim from the server taxonomy.
 *
 * @wiring-s777
 *   NO_API — prop-driven display component. Consumes RegistrySummaryPayload from C-01 PlanRegistry.
 *   ep 1 GET /api/v1/plans/registry is called by C-01; C-03 receives counts as props.
 *
 * @changelog
 *   1.0.0  S756  Initial build
 *   1.0.1  S777  Added JSDoc header. CRLF→LF.
 *   1.0.2  S801  Sub-count null guards added. EP 1 router returns only totalCount in envelope.
 *                Sub-count segments suppressed when null. Attention row suppressed when all null.
 *                OI-S801-ATTENTION-STRIP-SUBCOUNTS raised for future EP 1 sub-count extension.
 */

import React, { useCallback } from 'react';

/**
 * @typedef {{
 *   totalCount: number,
 *   governingCount: number,
 *   futureEffectiveCount: number,
 *   draftCount: number,
 *   awaitingReviewCount: number,
 *   submissionBlockerCount: number,
 *   readinessRiskCount: number,
 *   openAmendmentCount: number,
 * }} RegistrySummaryPayload
 */

/**
 * @typedef {{
 *   fiscalPeriodId: number | null,
 *   planStatus: string[],
 *   temporalRelationship: string[],
 *   roleIds: number[],
 *   segmentIds: number[],
 *   buIds: number[],
 *   primarySpine: string[],
 *   attentionLevel: string[],
 *   hasOpenAmendment: boolean | null,
 *   sourceType: string[],
 *   searchQuery: string,
 *   sortBy: string,
 *   sortDir: 'asc' | 'desc',
 * }} FilterState
 */

/**
 * Aggregate server-supplied plan and attention counts.
 *
 * Counts are never derived from rendered rows. Activating a count delegates
 * the corresponding filter update to the parent registry.
 *
 * @param {{
 *   summary: RegistrySummaryPayload | null,
 *   loading: boolean,
 *   onFilterApply: (partial: Partial<FilterState>) => void,
 *   className?: string
 * }} props
 */
export default function PlanAttentionStrip({
  summary,
  loading,
  onFilterApply,
  className = '',
}) {
  const handleKeyActivate = useCallback((event, action) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    action();
  }, []);

  if (loading || !summary) {
    return (
      <section
        className={`rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 ${className}`}
        aria-live="polite"
        aria-busy="true"
        aria-label="Loading plan registry counts"
      >
        <div className="flex items-center gap-3">
          <div className="skeleton h-3 w-16 rounded" />
          <div className="skeleton h-3 w-24 rounded" />
          <div className="skeleton h-3 w-28 rounded" />
          <div className="skeleton h-3 w-20 rounded" />
          <div className="skeleton h-3 w-28 rounded" />
        </div>

        <div className="mt-3 flex items-center gap-3">
          <div className="skeleton h-3 w-32 rounded" />
          <div className="skeleton h-3 w-28 rounded" />
          <div className="skeleton h-3 w-28 rounded" />
        </div>
      </section>
    );
  }

  const {
    totalCount,
    // Sub-counts are not returned by EP 1 registry envelope (only totalCount is).
    // They will be null until OI-S801-ATTENTION-STRIP-SUBCOUNTS is resolved.
    // Segments are suppressed (not rendered) when their count is null.
    governingCount = null,
    futureEffectiveCount = null,
    draftCount = null,
    awaitingReviewCount = null,
    submissionBlockerCount = null,
    readinessRiskCount = null,
    openAmendmentCount = null,
  } = summary;

  // Attention row only shown when at least one attention count is a real number > 0.
  const hasAttention =
    (submissionBlockerCount !== null && submissionBlockerCount > 0) ||
    (readinessRiskCount !== null && readinessRiskCount > 0) ||
    (openAmendmentCount !== null && openAmendmentCount > 0);

  const interactiveSegment = ({
    text,
    ariaLabel,
    onActivate,
    className: segmentClassName = '',
  }) => (
    <span
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onActivate}
      onKeyDown={(event) => handleKeyActivate(event, onActivate)}
      className={`cursor-pointer rounded px-1 py-0.5 text-[11px] underline-offset-2 hover:underline ${segmentClassName}`}
    >
      {text}
    </span>
  );

  return (
    <section
      className={`rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 ${className}`}
      aria-live="polite"
      aria-label="Plan registry summary"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-[rgb(var(--color-text-muted))]">
        <span>{totalCount.toLocaleString()} plans</span>

        {governingCount !== null && (
          <>
            <span aria-hidden="true">·</span>
            {interactiveSegment({
              text: `${governingCount.toLocaleString()} governing`,
              ariaLabel: `Show the ${governingCount} plans governing now`,
              onActivate: () =>
                onFilterApply({
                  temporalRelationship: ['governs_current'],
                }),
            })}
          </>
        )}

        {futureEffectiveCount !== null && (
          <>
            <span aria-hidden="true">·</span>
            {interactiveSegment({
              text: `${futureEffectiveCount.toLocaleString()} future effective`,
              ariaLabel: `Show the ${futureEffectiveCount} future effective plans`,
              onActivate: () =>
                onFilterApply({
                  temporalRelationship: ['future_effective'],
                }),
            })}
          </>
        )}

        {draftCount !== null && (
          <>
            <span aria-hidden="true">·</span>
            {interactiveSegment({
              text: `${draftCount.toLocaleString()} drafts`,
              ariaLabel: `Show the ${draftCount} draft plans`,
              onActivate: () =>
                onFilterApply({
                  planStatus: ['draft'],
                }),
            })}
          </>
        )}

        {awaitingReviewCount !== null && (
          <>
            <span aria-hidden="true">·</span>
            {interactiveSegment({
              text: `${awaitingReviewCount.toLocaleString()} awaiting review`,
              ariaLabel: `Show the ${awaitingReviewCount} plans awaiting approval review`,
              onActivate: () =>
                onFilterApply({
                  planStatus: ['pending_approval'],
                }),
            })}
          </>
        )}
      </div>

      {hasAttention && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          {submissionBlockerCount !== null && submissionBlockerCount > 0 &&
            interactiveSegment({
              text: `${submissionBlockerCount.toLocaleString()} submission blockers`,
              ariaLabel: `Show the ${submissionBlockerCount} plans with blocking attention`,
              onActivate: () =>
                onFilterApply({
                  attentionLevel: ['blocking'],
                }),
              className: 'text-[rgb(var(--color-danger))]',
            })}

          {submissionBlockerCount !== null && submissionBlockerCount > 0 && readinessRiskCount !== null && readinessRiskCount > 0 && (
            <span
              aria-hidden="true"
              className="text-[11px] text-[rgb(var(--color-text-muted))]"
            >
              ·
            </span>
          )}

          {readinessRiskCount !== null && readinessRiskCount > 0 &&
            interactiveSegment({
              text: `${readinessRiskCount.toLocaleString()} readiness risks`,
              ariaLabel: `Show the ${readinessRiskCount} plans with readiness risks`,
              onActivate: () =>
                onFilterApply({
                  attentionLevel: ['monitoring'],
                }),
              className: 'text-[rgb(var(--color-warning))]',
            })}

          {((submissionBlockerCount !== null && submissionBlockerCount > 0) || (readinessRiskCount !== null && readinessRiskCount > 0)) &&
            openAmendmentCount !== null && openAmendmentCount > 0 && (
              <span
                aria-hidden="true"
                className="text-[11px] text-[rgb(var(--color-text-muted))]"
              >
                ·
              </span>
            )}

          {openAmendmentCount !== null && openAmendmentCount > 0 &&
            interactiveSegment({
              text: `${openAmendmentCount.toLocaleString()} open amendments`,
              ariaLabel: `Show the ${openAmendmentCount} plans with open amendments`,
              onActivate: () =>
                onFilterApply({
                  hasOpenAmendment: true,
                }),
              className: 'text-[rgb(var(--color-info))]',
            })}
        </div>
      )}
    </section>
  );
}
